import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useAppDispatch } from "@store/hooks";
import { useNotification } from "@shared/contexts/NotificationContext";
import { assignResource, AssignResourcePayload } from "@services/api";
import type { WorkspaceResourceView } from "@/types/workspace";
import { loadAllWorkspaceReadings } from "@features/workspace-readings/state/workspaceReadingsSlice";
import { loadAllWorkspaceCanvases } from "@features/workspace-canvases/state/workspaceCanvasesSlice";
import { loadAllWorkspacePapers } from "@features/workspace-papers/state/workspacePapersSlice";

type Kind = "optional" | "required" | "graded";

interface Props {
  open: boolean;
  rows: WorkspaceResourceView[];
  workspaceId: string;
  onClose: () => void;
  onSaved: () => void;
}

// Date + time are split across two inputs (rather than one
// datetime-local, which yields "" unless BOTH are filled — silently
// dropping date-only picks). The API gives an ISO instant.
const pad = (n: number) => String(n).padStart(2, "0");

// Split an ISO instant into the date + time the inputs want, in the
// viewer's local timezone.
const splitDue = (iso: string | null): { date: string; time: string } => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

// Combine the date + optional time into an ISO instant (local → UTC).
// An empty time means "due end of that day" — the conventional reading
// of a date-only due date — so it defaults to 23:59 local.
const combineDue = (date: string, time: string): string => {
  const [y, m, d] = date.split("-");
  const [hh, mm] = (time || "23:59").split(":");
  return new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(hh),
    Number(mm)
  ).toISOString();
};

const KIND_HELP: Record<Kind, string> = {
  optional: "Reference material. Not tracked as an assignment.",
  required: "Expected work. Tracked, but no formal submission.",
  graded: "Students turn it in and receive a grade.",
};

export default function AssignResourceModal({
  open,
  rows,
  workspaceId,
  onClose,
  onSaved,
}: Props) {
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useNotification();
  const isBulk = rows.length > 1;
  const single = rows.length === 1 ? rows[0] : null;

  const [kind, setKind] = useState<Kind>("required");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  // Explicit "remove the existing due date" intent. Distinct from a
  // blank date, which means "leave it untouched" — the JSON tri-state
  // the backend needs (sent as due_date: "" to signal NULL).
  const [cleared, setCleared] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prefill from the single row; for bulk start from a sensible default
  // (changes apply to every selected resource).
  useEffect(() => {
    if (!open) return;
    setKind(((single?.assignmentKind as Kind) ?? "required") || "required");
    const { date, time } = splitDue(single?.dueDate ?? null);
    setDueDate(date);
    setDueTime(time);
    setCleared(false);
    setSubmitting(false);
  }, [open, single]);

  const refetchAffected = () => {
    const types = new Set(rows.map((r) => r.type));
    if (types.has("reading")) dispatch(loadAllWorkspaceReadings(workspaceId));
    if (types.has("canvas")) dispatch(loadAllWorkspaceCanvases(workspaceId));
    if (types.has("paper")) dispatch(loadAllWorkspacePapers(workspaceId));
  };

  const handleSave = async () => {
    setSubmitting(true);
    const payload: AssignResourcePayload = { assignmentKind: kind };
    // Due date is tri-state: a date entered → set it; an explicit Clear
    // → "" (remove it); blank + not cleared → omit (leave untouched).
    // Time is optional (defaults to end of day inside combineDue).
    if (dueDate) payload.dueDate = combineDue(dueDate, dueTime);
    else if (cleared) payload.dueDate = "";

    const results = await Promise.allSettled(
      rows.map((r) => assignResource(r.type, workspaceId, r.id, payload))
    );
    const failures = results.filter(
      (res) => res.status === "rejected" || !res.value?.success
    );
    setSubmitting(false);

    if (failures.length > 0) {
      showError(
        `Could not update ${failures.length} of ${rows.length} resource${
          rows.length === 1 ? "" : "s"
        }.`
      );
      return;
    }
    showSuccess(
      isBulk ? `Updated ${rows.length} resources.` : "Assignment updated."
    );
    refetchAffected();
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isBulk
          ? `Assign ${rows.length} resources`
          : `Assign “${single?.name || "Untitled"}”`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Assignment type
            </Typography>
            <ToggleButtonGroup
              value={kind}
              exclusive
              fullWidth
              onChange={(_, v: Kind | null) => v && setKind(v)}
              size="small"
              color="primary"
            >
              <ToggleButton value="optional">Optional</ToggleButton>
              <ToggleButton value="required">Required</ToggleButton>
              <ToggleButton value="graded">Graded</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {KIND_HELP[kind]}
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Due date"
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  // Entering a date is not a clear.
                  if (e.target.value) setCleared(false);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Due time"
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                // Time alone is meaningless; gate it on a date being set.
                disabled={!dueDate}
                sx={{ width: 150 }}
              />
              {/* Remove an existing due date (single resource only — bulk
                  blank already means "leave each one alone"). */}
              {!isBulk && (dueDate || cleared) && (
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => {
                    setDueDate("");
                    setDueTime("");
                    setCleared(true);
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {cleared
                ? "Due date will be removed on save."
                : isBulk
                  ? "Applies to all selected. Leave the date blank to keep existing due dates. Time defaults to end of day (11:59 PM)."
                  : "Leave the date blank to keep the current due date. Time is optional — defaults to end of day (11:59 PM)."}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {submitting ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
